/**
 * useReturnTo (TMMPDM-0035, NAV-011 / UI-009) — return to the list you left. A roster row links to
 * its record with `state: { from }` (the list's path and query); the record's first breadcrumb
 * ("Tenants", "Resellers") links back to `from`, so the filters and page come back with it.
 *
 * `from` is honoured only when it is that list's own path — a record opened from anywhere else
 * (the dashboard, a payout row, a pasted link) breadcrumbs to the plain list. Scroll position is
 * not restored (no storage entry is sanctioned for it).
 */
import { useLocation } from 'react-router-dom';

export interface ReturnState {
  from: string;
}

function fromOf(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('from' in state)) return null;
  const { from } = state as { from: unknown };
  return typeof from === 'string' ? from : null;
}

export function useReturnTo(): {
  /** Link `state` for a row on the current list. */
  linkState: ReturnState;
  /** The breadcrumb destination back to `listPath`: the list as it was left, or the plain list. */
  backTo: (listPath: string) => string;
} {
  const location = useLocation();
  const from = fromOf(location.state);
  return {
    linkState: { from: location.pathname + location.search },
    backTo: (listPath) =>
      from !== null && (from === listPath || from.startsWith(`${listPath}?`)) ? from : listPath,
  };
}
